import axios from 'axios'
import type { AxiosError, AxiosHeaders, InternalAxiosRequestConfig } from 'axios'

/** Base del API sin barras finales duplicadas; asegura un solo sufijo `/api`. */
function resolveApiUrl(): string {
  let raw = (import.meta.env.VITE_API_URL as string | undefined)?.trim()
  if (raw === 'undefined' || raw === 'null') {
    raw = ''
  }
  const base = (raw && raw.length > 0 ? raw : 'http://localhost:8080').replace(
    /\/+$/,
    ''
  )
  return base.endsWith('/api') ? base : `${base}/api`
}

/** No forzar recarga en /login si el 401 es del intento de inicio de sesión (mostrar mensaje en el formulario). */
function isAuthAttempt401(error: AxiosError): boolean {
  if (error.response?.status !== 401) return false
  const cfg = error.config as InternalAxiosRequestConfig | undefined
  if (!cfg) return false
  const method = (cfg.method || 'get').toLowerCase()
  if (method !== 'post') return false
  const url = cfg.url || ''
  return (
    url === '/auth/login' ||
    url.endsWith('/auth/login') ||
    url.includes('/auth/webauthn/login/finish')
  )
}

export const API_URL = resolveApiUrl()

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * La instancia `api` fuerza `Content-Type: application/json`. Con `FormData`, axios convertiría
 * el cuerpo a JSON; hay que quitar ese header para que el navegador envíe multipart con boundary.
 */
export const axiosFormDataRequestConfig: Pick<InternalAxiosRequestConfig, 'transformRequest'> = {
  transformRequest: [
    (data, headers) => {
      if (data instanceof FormData && headers) {
        const h = headers as AxiosHeaders
        if (typeof h.delete === 'function') {
          h.delete('Content-Type')
        }
      }
      return data
    },
  ],
}

// Interceptor para agregar token JWT
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

/** Refresh en vuelo compartido: varios 401 concurrentes esperan una sola renovación. */
let refreshPromise: Promise<string | null> | null = null

function clearSessionAndRedirect(): void {
  localStorage.removeItem('token')
  localStorage.removeItem('refreshToken')
  if (window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

/** Renueva el access token con el refresh token guardado. Devuelve el nuevo token o null si falla. */
async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) return null
  try {
    // axios "crudo" (sin interceptores) para no recursar sobre el propio 401.
    const res = await axios.post(`${API_URL}/auth/refresh`, { refreshToken })
    const data = res.data?.data
    const newToken: string | undefined = data?.token
    const newRefresh: string | undefined = data?.refreshToken
    if (!newToken) return null
    localStorage.setItem('token', newToken)
    if (newRefresh) localStorage.setItem('refreshToken', newRefresh)
    return newToken
  } catch {
    return null
  }
}

// Interceptor para manejar errores: intenta renovar el access token ante un 401 antes de desloguear.
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined

    if (error.response?.status !== 401 || !config || isAuthAttempt401(error)) {
      return Promise.reject(error)
    }
    // No reintentar el propio refresh ni una request ya reintentada.
    const url = config.url || ''
    if (config._retried || url.includes('/auth/refresh')) {
      clearSessionAndRedirect()
      return Promise.reject(error)
    }

    config._retried = true
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null
      })
    }
    const newToken = await refreshPromise
    if (!newToken) {
      clearSessionAndRedirect()
      return Promise.reject(error)
    }
    config.headers.Authorization = `Bearer ${newToken}`
    return api(config)
  }
)
